import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { useSelector } from 'react-redux';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import './DashboardLayout.css';

const DashboardLayout = memo(({ children, onShowNotifications }) => {
    const { tab, subtab } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    // Use cached permissions to avoid repeated checks
    const { hasShowInsight, hasShowProject, permissionsLoading } = useCachedPermissions();

    // Store the last valid tab to prevent unwanted redirects
    const lastValidTabRef = React.useRef(null);

    // Track component lifecycle
    useEffect(() => {
        return () => {
        };
    }, []);

    // Determine initial tab from URL
    const getInitialTab = useCallback(() => {
        // Extract path parts from the current URL
        const pathParts = location.pathname.split('/').filter(part => part !== '');

        // If no path parts, default to first available tab based on permissions
        if (pathParts.length === 0) {
            const defaultTab = hasShowInsight ? 'overview' : 'projects';
            console.log('🏠 No path parts - hasShowInsight:', hasShowInsight, '-> defaultTab:', defaultTab);
            return defaultTab;
        }

        // Handle dashboard routes with parameters (/dashboard/tab or /dashboard/tab/subtab)
        if (pathParts[0] === 'dashboard') {
            if (pathParts.length === 3) {
                // /dashboard/tab/subtab
                return `${pathParts[1]}-${pathParts[2]}`;
            } else if (pathParts.length === 2) {
                // /dashboard/tab
                return pathParts[1];
            } else {
                // Just /dashboard - default to first available tab based on permissions
                const defaultTab = hasShowInsight ? 'overview' : 'projects';
                console.log('📊 Dashboard route - hasShowInsight:', hasShowInsight, '-> defaultTab:', defaultTab);
                return defaultTab;
            }
        }

        // Handle direct sub-routes like /finance/paid, /tickets/requests, etc.
        if (pathParts.length === 2) {
            const [mainTab, subTab] = pathParts;

            // Special case for customer profile: /customer/:customerId
            if (mainTab === 'customer') {
                return 'customer-profile';
            }

            // Special case for add unit to customer: /add-unit/:customerId
            if (mainTab === 'add-unit') {
                return 'add-unit-to-customer';
            }

            // Special case for add customer to unit: /add-customer/:unitId
            if (mainTab === 'add-customer') {
                return 'add-customer-to-unit';
            }

            // Special case for customers/groups: /customers/groups
            if (mainTab === 'customers' && subTab === 'groups') {
                return 'groups';
            }

            // Check if it's a valid subtab combination
            const validSubTabs = {
                'finance': ['paid', 'unpaid'],
                'maintenance': ['paid', 'unpaid'],
                'utility': ['paid', 'unpaid'],
                'tickets': ['requests', 'closed', 'categories'],
                'logs': ['admin', 'user']
            };

            if (validSubTabs[mainTab] && validSubTabs[mainTab].includes(subTab)) {
                return `${mainTab}-${subTab}`;
            }
        }

        // Handle 3-part routes like /unit/:unitId/update-installments or /projects/project/:projectId
        if (pathParts.length === 3) {
            const [mainTab, type, id] = pathParts;

            // Special case for update installments: /unit/:unitId/update-installments
            if (mainTab === 'unit' && pathParts[2] === 'update-installments') {
                return 'add-customer-to-unit';
            }

            // Special case for project views: /projects/project/:projectId
            if (mainTab === 'projects' && type === 'project') {
                return 'projects';
            }

            // Special case for project units: /projects/project/:projectId (when viewing units)
            if (mainTab === 'projects' && type === 'unit') {
                return 'projects';
            }
        }

        // Handle 4-part routes like /projects/project/:projectId/units
        if (pathParts.length === 4) {
            const [mainTab, type, id, view] = pathParts;

            // Special case for project units view: /projects/project/:projectId/units
            if (mainTab === 'projects' && type === 'project' && view === 'units') {
                return 'projects';
            }
        }

        // Handle single direct routes like /overview, /projects, etc.
        if (pathParts.length === 1) {
            const pathTab = pathParts[0];
            const validSingleTabs = ['overview', 'projects', 'protocols', 'customers', 'groups', 'myclub', 'updates', 'settings', 'finance', 'maintenance', 'utility', 'tickets', 'logs'];
            if (validSingleTabs.includes(pathTab)) {
                return pathTab;
            }
        }

        // Default fallback - return first available tab based on permissions
        const fallbackTab = hasShowInsight ? 'overview' : 'projects';
        console.log('🔄 Fallback - hasShowInsight:', hasShowInsight, '-> fallbackTab:', fallbackTab);
        return fallbackTab;
    }, [location.pathname, hasShowInsight]);

    const [selectedTab, setSelectedTab] = useState(() => {
        const initialTab = getInitialTab();
        // Store the initial tab as last valid tab
        if (initialTab !== 'overview' || location.pathname === '/overview') {
            lastValidTabRef.current = initialTab;
        }
        return initialTab;
    });

    // Aggressive protection against unwanted /dashboard redirects
    useEffect(() => {
        if (location.pathname === '/dashboard' && lastValidTabRef.current && lastValidTabRef.current !== 'overview') {
            console.log('🔄 BLOCKING /dashboard REDIRECT - Redirecting to last valid tab:', lastValidTabRef.current);
            const targetPath = lastValidTabRef.current.includes('-') ?
                `/${lastValidTabRef.current.split('-')[0]}/${lastValidTabRef.current.split('-')[1]}` :
                `/${lastValidTabRef.current}`;
            navigate(targetPath, { replace: true });
            return;
        }

        // Handle default dashboard access without permission
        if (location.pathname === '/dashboard' && !hasShowInsight) {
            console.log('🚫 DASHBOARD ACCESS DENIED - Redirecting to projects (no show_insight permission)');
            navigate('/projects', { replace: true });
            return;
        }
    }, [location.pathname, navigate, hasShowInsight]);

    // Update selectedTab when URL changes
    useEffect(() => {
        const newTab = getInitialTab();

        // Check if user is trying to access overview without permission
        // Don't redirect while permissions are loading to prevent false redirects
        if (newTab === 'overview' && !hasShowInsight && !permissionsLoading && location.pathname === '/overview') {
            navigate('/projects', { replace: true });
            return;
        }

        // Update last valid tab reference
        if (newTab !== 'overview' || location.pathname === '/overview') {
            lastValidTabRef.current = newTab;
        }

        if (newTab !== selectedTab) {
            setSelectedTab(newTab);
        } else {
        }
    }, [getInitialTab, selectedTab, hasShowInsight, navigate]); // Add hasShowInsight to dependencies

    const handleTabSelect = (tabName) => {
        // This is mainly for backward compatibility
        // The Sidebar now handles navigation directly
        setSelectedTab(tabName);
        setIsMobileSidebarOpen(false); // Close sidebar on mobile after selection
    };

    const toggleMobileSidebar = () => {
        setIsMobileSidebarOpen(prev => !prev);
    };

    return (
        <div className="dashboard-layout">
            <Sidebar 
                selectedTab={selectedTab} 
                onTabSelect={handleTabSelect} 
                isOpen={isMobileSidebarOpen}
                onClose={() => setIsMobileSidebarOpen(false)}
            />
            {isMobileSidebarOpen && (
                <div 
                    className="sidebar-overlay" 
                    onClick={() => setIsMobileSidebarOpen(false)}
                />
            )}
            <main className="dashboard-main">
                <DashboardHeader 
                    onShowNotifications={onShowNotifications} 
                    onToggleSidebar={toggleMobileSidebar}
                />
                <div className="dashboard-content">
                    {typeof children === 'function' ? children(selectedTab) : children}
                </div>
            </main>
        </div>
    );
});

export default DashboardLayout; 